import React, { useEffect, useMemo, useState } from 'react';
import { LuLoaderPinwheel } from 'react-icons/lu';
import { Link, useNavigate } from 'react-router-dom';

import { TRANSFER_PROVIDER_LABEL, TransferProvider } from '../../entities/transfer';
import { fetchIntegrations } from '../../shared/api/integration';
import { createTransferJob } from '../../shared/api/transfer';
import { Modal } from '../ui/Modal';

const ALL_PROVIDERS: TransferProvider[] = ['jira', 'trello', 'standalone'];

interface TransferModalProps {
  sourceProvider: TransferProvider;
  sourceProjectId: string;
  sourceProjectName?: string;
  onClose: () => void;
}

const TransferModal: React.FC<TransferModalProps> = ({
  sourceProvider,
  sourceProjectId,
  sourceProjectName,
  onClose,
}) => {
  const navigate = useNavigate();
  const [isCheckingProviders, setIsCheckingProviders] = useState(true);
  const [isJiraConnected, setIsJiraConnected] = useState(false);
  const [isTrelloConnected, setIsTrelloConnected] = useState(false);
  const [targetProvider, setTargetProvider] = useState<TransferProvider | ''>('');
  const [targetProjectName, setTargetProjectName] = useState(sourceProjectName ? `${sourceProjectName} (copy)` : '');
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchIntegrations()
      .then((d) => {
        if (cancelled) return;
        setIsJiraConnected(!!d?.isJiraConnected);
        setIsTrelloConnected(!!d?.isTrelloConnected);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setIsCheckingProviders(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Only offer a target the user can actually transfer into — Jira/Trello need a live
  // connection, Standalone doesn't (it just creates a new project).
  const targetOptions = useMemo(
    () =>
      ALL_PROVIDERS.filter((p) => {
        if (p === sourceProvider) return false;
        if (p === 'jira') return isJiraConnected;
        if (p === 'trello') return isTrelloConnected;
        return true;
      }),
    [sourceProvider, isJiraConnected, isTrelloConnected],
  );

  useEffect(() => {
    if (targetOptions.length > 0 && !targetOptions.includes(targetProvider as TransferProvider)) {
      setTargetProvider(targetOptions[0]);
    }
  }, [targetOptions, targetProvider]);

  const handleCreate = async () => {
    if (!targetProvider) return;
    setIsCreating(true);
    setError(null);
    try {
      const job = await createTransferJob({
        sourceProvider,
        targetProvider,
        sourceProjectId,
        targetProjectName: targetProjectName.trim() || undefined,
      });
      navigate(`/transfer/${job._id}`);
    } catch {
      setError('Failed to start transfer.');
      setIsCreating(false);
    }
  };

  return (
    <Modal onClose={onClose} className="max-w-md" labelledBy="transfer-modal-title">
      <div className="card-body p-5 gap-3">
        <div id="transfer-modal-title" className="text-lg font-semibold">
          Transfer{sourceProjectName ? ` "${sourceProjectName}"` : ''} to…
        </div>

        {isCheckingProviders ? (
          <div className="flex justify-center py-6">
            <LuLoaderPinwheel className="animate-spin opacity-40" size={20} />
          </div>
        ) : targetOptions.length === 0 ? (
          <>
            <p className="text-sm opacity-70">
              None of your other providers are connected yet — there&apos;s nowhere to transfer this project to.
            </p>
            <div className="alert alert-warning text-sm py-2">
              <span>Connect at least one provider to transfer a project.</span>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button className="btn btn-ghost btn-sm" onClick={onClose}>
                Cancel
              </button>
              <Link to="/integrations" className="btn btn-primary btn-sm" onClick={onClose}>
                Go to integrations
              </Link>
            </div>
          </>
        ) : (
          <>
            <p className="text-sm opacity-70">
              Copies the whole project to a brand-new {TRANSFER_PROVIDER_LABEL[targetProvider || targetOptions[0]]}{' '}
              project. The source is never touched — this is copy, not move.
            </p>

            {error && (
              <div className="alert alert-error py-2">
                <span className="text-sm">{error}</span>
              </div>
            )}

            <label className="form-control">
              <span className="label-text text-xs mb-1">Target platform</span>
              <select
                className="select select-bordered select-sm"
                value={targetProvider}
                onChange={(e) => setTargetProvider(e.target.value as TransferProvider)}
              >
                {targetOptions.map((p) => (
                  <option key={p} value={p}>
                    {TRANSFER_PROVIDER_LABEL[p]}
                  </option>
                ))}
              </select>
            </label>

            <label className="form-control">
              <span className="label-text text-xs mb-1">New project name (optional)</span>
              <input
                className="input input-bordered input-sm w-full"
                placeholder={sourceProjectName ? `${sourceProjectName} (copy)` : 'Target project name'}
                value={targetProjectName}
                onChange={(e) => setTargetProjectName(e.target.value)}
              />
            </label>

            <div className="flex justify-end gap-2 pt-2">
              <button className="btn btn-ghost btn-sm" onClick={onClose} disabled={isCreating}>
                Cancel
              </button>
              <button className="btn btn-primary btn-sm" onClick={handleCreate} disabled={isCreating}>
                {isCreating && <LuLoaderPinwheel className="animate-spin" size={14} />}
                Start transfer
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default TransferModal;
